import * as React from 'react';
import { useState } from 'react';
import {
  TopToolbar,
  useGetIdentity,
  useNotify,
  useRecordContext,
  useRefresh,
  Identifier,
  RaRecord,
} from 'react-admin';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { OwnerSelector } from '../components/OwnerSelector';
import { takeLead } from '../inbox/inboxApi';

interface LeadRecord extends RaRecord {
  owner_id?: Identifier | null;
}

/**
 * "Take it" on a lead nobody owns yet. First click wins — see `takeLead`,
 * which the unclaimed-lead alert's Take it shares.
 */
export function TakeLeadButton({ className }: { className?: string } = {}) {
  const record = useRecordContext<LeadRecord>();
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const refresh = useRefresh();
  const [saving, setSaving] = useState(false);

  if (!record || record.owner_id || !identity?.id) return null;

  const take = async () => {
    setSaving(true);
    try {
      const won = await takeLead(String(record.id), String(identity.id));
      notify(won ? 'Lead is yours' : 'Someone else already has this lead', {
        type: won ? 'success' : 'info',
      });
    } catch (err: any) {
      notify(`Failed to take lead: ${err?.message ?? err}`, { type: 'error' });
    } finally {
      setSaving(false);
    }
    refresh();
  };

  return (
    <Button
      size="sm"
      className={cn('cleo-ui', className)}
      disabled={saving}
      onClick={() => void take()}
    >
      Take it
    </Button>
  );
}

/**
 * The lead owner controls, in the order they sit wherever a lead shows its
 * owner: the shared owner chip (same component as the case's), plus "Take it"
 * while nobody owns it.
 */
export function LeadOwnerControls({ compact = false }: { compact?: boolean }) {
  return (
    <>
      <OwnerSelector resource="leads" noun="lead" compact={compact} />
      <TakeLeadButton className={compact ? 'h-6 px-2 text-xs' : undefined} />
    </>
  );
}

/**
 * Desktop top-right toolbar of LeadShow — the same spot FuneralShowToolBar puts
 * the case owner. No Edit button: leads have no edit view.
 */
export const LeadShowToolBar = () => (
  <TopToolbar>
    <LeadOwnerControls />
  </TopToolbar>
);

export default LeadShowToolBar;
